import React, { useState, useEffect } from 'react';
import { Outlet, NavLink, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';

// Define nav items with required roles (empty = all roles can see)
const allNavItems = [
  { to: '/',           icon: '📊', label: 'Dashboard',       roles: ['admin', 'supervisor', 'guard'] },
  { to: '/map',        icon: '🗺️',  label: 'Live Map',        roles: ['admin', 'supervisor'] },
  { to: '/patrols',    icon: '🚶', label: 'Patrols',         roles: ['admin', 'supervisor', 'guard'] },
  { to: '/shifts',     icon: '📅', label: 'Shift Roster',    roles: ['admin', 'supervisor'] },
  { to: '/routes',     icon: '📍', label: 'Routes',          roles: ['admin', 'supervisor'] },
  { to: '/sites',      icon: '🏢', label: 'Sites',            roles: ['admin', 'supervisor'] },
  { to: '/geofencing', icon: '🛡️', label: 'Geofencing',      roles: ['admin', 'supervisor'] },
  { to: '/guards',     icon: '👮', label: 'Guards & Staff',  roles: ['admin', 'supervisor'] },
  { to: '/incidents',  icon: '⚠️',  label: 'Incidents' },
  { to: '/reports',    icon: '📋', label: 'Reports',         roles: ['admin', 'supervisor'] },
  { to: '/report-builder', icon: '📊', label: 'Report Builder',  roles: ['admin', 'supervisor'] },
  { to: '/simulator',  icon: '🎮', label: 'Guard Simulator', roles: ['admin', 'supervisor', 'guard'] },
  { to: '/settings',   icon: '⚙️',  label: 'Settings',        roles: ['admin'], dividerBefore: true },
];

export default function Layout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);
  const [logoUrl, setLogoUrl]   = useState('');
  const [appName, setAppName]   = useState('PatrolTrack');

  // Load logo + app name from settings
  useEffect(() => {
    axios.get('/api/settings').then(res => {
      const general = res.data?.general || {};
      setLogoUrl(general.logo_url?.value || '');
      setAppName(general.app_name?.value || 'PatrolTrack');
    }).catch(() => {});
  }, []);

  const handleLogout = () => { logout(); navigate('/login'); };
  const closeMenu = () => setMenuOpen(false);

  // Filter nav items based on user role
  const navItems = allNavItems.filter(item =>
    !item.roles || item.roles.includes(user?.role)
  );

  const roleColors = { admin: '#ef233c', supervisor: '#f59e0b', guard: '#22c55e' };
  const roleColor = roleColors[user?.role] || '#9ca3af';

  return (
    <div>
      {/* Mobile Top Bar */}
      <div className="mobile-topbar">
        <button className="hamburger" onClick={() => setMenuOpen(true)} aria-label="Open menu">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
            <line x1="3" y1="6" x2="21" y2="6"/>
            <line x1="3" y1="12" x2="21" y2="12"/>
            <line x1="3" y1="18" x2="21" y2="18"/>
          </svg>
        </button>
        {/* Logo only — no app name text to save space */}
        <span className="mobile-topbar-title" style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          {logoUrl
            ? <img src={logoUrl} alt="logo" style={{ height: 30, maxWidth: 120, width: 'auto', objectFit: 'contain', borderRadius: 4 }} />
            : <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>🛡️ <span>{appName}</span></span>
          }
        </span>
        {/* User badge */}
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 2 }}>
          <span style={{ fontSize: 12, color: 'white', fontWeight: 600, lineHeight: 1 }}>
            {user?.name?.split(' ')[0]}
          </span>
          <span style={{
            fontSize: 9, color: 'white', fontWeight: 700,
            textTransform: 'uppercase', letterSpacing: .5,
            background: roleColor, borderRadius: 4,
            padding: '1px 5px', lineHeight: 1.4,
          }}>{user?.role}</span>
        </div>
      </div>

      {/* Overlay */}
      <div className={`sidebar-overlay ${menuOpen ? 'open' : ''}`} onClick={closeMenu} />

      {/* Sidebar */}
      <aside className={`sidebar ${menuOpen ? 'open' : ''}`} role="navigation">
        <div className="sidebar-logo">
          {logoUrl ? (
            <img src={logoUrl} alt="logo"
              style={{ height: 36, width: 'auto', maxWidth: 140, objectFit: 'contain', borderRadius: 8 }} />
          ) : (
            <span style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              🛡️ {appName}
            </span>
          )}
          <button className="sidebar-logo-close" onClick={closeMenu} aria-label="Close menu">✕</button>
        </div>

        <nav className="sidebar-nav">
          {navItems.map(item => (
            <React.Fragment key={item.to}>
              {item.dividerBefore && (
                <div style={{ height: 1, background: 'rgba(255,255,255,0.1)', margin: '6px 12px' }} />
              )}
              <NavLink
                to={item.to}
                end={item.to === '/'}
                onClick={closeMenu}
                aria-label={item.label}
              >
                <span className="icon" aria-hidden="true">{item.icon}</span>
                {item.label}
              </NavLink>
            </React.Fragment>
          ))}
        </nav>

        <div className="sidebar-footer">
          <div className="sidebar-user">
            <strong>👤 {user?.name}</strong>
            <span style={{
              display: 'inline-block',
              marginTop: 4,
              padding: '2px 8px',
              borderRadius: 20,
              fontSize: 10,
              fontWeight: 700,
              textTransform: 'uppercase',
              letterSpacing: '0.05em',
              background: roleColor,
              color: 'white'
            }}>{user?.role}</span>
          </div>
          <button onClick={handleLogout} className="btn btn-danger btn-sm btn-block">
            Logout
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="main-content">
        <Outlet />
      </main>
    </div>
  );
}
